import { Location, locationName } from '@cobliteam/common-types';
import { lazy } from '@cobliteam/lazy-with-retries';
import { Redirect } from 'react-router-dom';

import CoachingPage from '../pages/CoachingPage';
import NewMediaPage from '../pages/NewMediaPage';

const MediaLandingPage = lazy(() => import('../pages/MediaLandingPage'));
const MultiCamLandingPage = lazy(() => import('../pages/MultiCamLandingPage'));
const MediaEventPage = lazy(() => import('../pages/MediaEventPage'));
const MediaDetailsPage = lazy(() => import('../pages/MediaDetailsPage'));
const MediaRequestPage = lazy(() => import('../pages/MediaRequestPage'));
const TripImageHistoryPage = lazy(() => import('../pages/TripImageHistoryPage'));
const CamerasHealthPage = lazy(() => import('../pages/CamerasHealthPage'));
const CoachingFeedbacksPage = lazy(() => import('../pages/CoachingFeedbacksPage'));
const CoachingFeedbackDetailsPage = lazy(() => import('../pages/CoachingFeedbackDetailsPage'));

const routes = [
  {
    path: Location.CAMERA_LP,
    team: 'squad-video-xp',
    component: MediaLandingPage,
    exact: true,
  },
  {
    path: Location.CAMERA_EVENT_ALL_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/events' />,
    exact: true,
  },
  {
    path: Location.CAMERA_EVENT_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/events' />,
    exact: true,
  },
  {
    path: Location.CAMERA_EVENT_FAVORITE_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/events/favorites' />,
    exact: true,
  },
  {
    path: Location.CAMERA_REQUEST_REDIRECT,
    team: 'squad-video-xp',
    component: () => {
      const queryParams = new URLSearchParams(window.location.search);
      const mediaId = queryParams.get('media_id');

      if (mediaId) {
        queryParams.delete('media_id');
        queryParams.set('id', mediaId);

        return <Redirect to={`/camera/requests?${queryParams.toString()}`} />;
      }
      return <Redirect to='/camera/requests' />;
    },
    exact: true,
  },

  {
    path: Location.CAMERA_REQUEST_ALL_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/requests' />,
    exact: true,
  },
  {
    path: Location.CAMERA_REQUESTS_FAVORITE_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/requests/favorites' />,
    exact: true,
  },
  {
    path: Location.CAMERA_TRIP_IMAGE_PENDING_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/images' />,
    exact: true,
  },
  {
    path: Location.CAMERA_TRIP_IMAGE_ALL_REDIRECT,
    team: 'squad-video-xp',
    component: () => <Redirect to='/camera/images/all' />,
    exact: true,
  },
  {
    path: Location.CAMERA,
    team: 'squad-video-xp',
    component: NewMediaPage,
    accessDeniedDescription: 'ver vídeos da câmera',
    needsGoogleMaps: true,
    routes: [
      {
        path: Location.CAMERA_TRIP_IMAGE_HISTORY_ALL,
        component: TripImageHistoryPage,
        accessDeniedDescription: 'ver histórico de imagens',
        exact: true,
      },
      {
        path: Location.CAMERA_TRIP_IMAGE_HISTORY,
        component: TripImageHistoryPage,
        accessDeniedDescription: 'ver histórico de imagens',
        exact: true,
      },
      {
        path: Location.CAMERA_REQUESTS_FAVORITES,
        title: locationName[Location.CAMERA_REQUESTS],
        component: MediaRequestPage,
        accessDeniedDescription: 'ver vídeos solicitados da câmera',
        exact: true,
      },
      {
        // Aba "Solicitados automaticamente" removida (VXP-903) — quem tinha o link salvo cai na lista da frota.
        path: Location.CAMERA_REQUESTS_AUTOMATIC_REDIRECT,
        component: () => <Redirect to={Location.CAMERA_REQUESTS} />,
        exact: true,
      },
      {
        path: Location.CAMERA_REQUESTS,
        title: locationName[Location.CAMERA_REQUESTS],
        component: MediaRequestPage,
        accessDeniedDescription: 'ver vídeos solicitados da câmera',
        exact: true,
      },
      {
        path: Location.CAMERA_REQUESTS_DETAILS,
        title: locationName[Location.CAMERA_REQUESTS_DETAILS],
        component: MediaDetailsPage,
        accessDeniedDescription: 'ver vídeos solicitados da câmera',
        exact: true,
      },
      {
        path: Location.CAMERA_EVENTS,
        title: locationName[Location.CAMERA_EVENTS],
        component: MediaEventPage,
        accessDeniedDescription: 'ver vídeos da câmera',
        exact: true,
      },
      {
        path: Location.CAMERA_EVENTS_FAVORITES,
        title: locationName[Location.CAMERA_EVENTS],
        component: MediaEventPage,
        accessDeniedDescription: 'ver vídeos da câmera',
        exact: true,
      },
      {
        path: Location.CAMERA_EVENTS_DASHBOARD,
        title: locationName[Location.CAMERA_EVENTS],
        component: MediaEventPage,
        accessDeniedDescription: 'ver análises da câmera',
        exact: true,
      },
      {
        path: Location.CAMERA_EVENTS_DETAILS,
        title: locationName[Location.CAMERA_EVENTS_DETAILS],
        component: MediaDetailsPage,
        accessDeniedDescription: 'ver detalhes do vídeo',
        exact: true,
      },
      {
        path: Location.CAMERA_HEALTH_OBSTRUCTION,
        title: locationName[Location.CAMERA_HEALTH_OBSTRUCTION],
        component: CamerasHealthPage,
        accessDeniedDescription: 'ver saúde das cãmeras',
        exact: true,
        featureFlag: 'showObstructionDashboard',
      },
    ],
  },
  {
    path: Location.COACHING,
    team: 'squad-video-xp',
    component: () => <Redirect to={Location.COACHING_ONGOING} />,
    exact: true,
  },
  {
    path: Location.COACHING_ROOT,
    team: 'squad-video-xp',
    component: CoachingPage,
    needsGoogleMaps: true,
    routes: [
      {
        path: Location.COACHING_ONGOING,
        component: CoachingFeedbacksPage,
        accessDeniedDescription: 'ver tratativas de ocorrências',
        exact: true,
      },
      {
        path: Location.COACHING_FINISHED,
        component: CoachingFeedbacksPage,
        accessDeniedDescription: 'ver tratativas de ocorrências',
        exact: true,
      },
      {
        path: Location.COACHING_DETAILS,
        component: CoachingFeedbackDetailsPage,
        accessDeniedDescription: 'ver detalhes da tratativa',
        exact: true,
      },
    ],
  },
];

export default routes;
